import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, MapPin, RefreshCw, Mail, StickyNote, ListChecks, Send, CheckSquare, Activity, Paperclip, Trophy, GitCommitHorizontal, TrendingUp, TrendingDown } from "lucide-react";
import { api } from "@/lib/api";
import { fmtCompact, greeting, todayLong, relTime } from "@/lib/format";
import { stageColor } from "@/lib/stages";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { StageBadge, Money, Skeleton, EmptyState, CompanyLogo, UserAvatar } from "@/components/common";
import { AreaTrend } from "@/components/charts/AreaTrend";
import { useAuth } from "@/context/AuthContext";

const ACT_ICON = { email: Mail, meeting: StickyNote, note: StickyNote, requirement: ListChecks, quote: Send, quote_sent: Send, task: CheckSquare, document: Paperclip, won: Trophy, stage: GitCommitHorizontal };

function SectionHead({ title, description, right, count, className }) {
  return (
    <div className={cn("mb-4 flex items-end justify-between gap-4", className)}>
      <div>
        <h2 className="flex items-center gap-2 text-[15px] font-semibold tracking-[-0.01em]">
          {title}
          {typeof count === "number" ? <span className="rounded-full bg-black/[0.06] px-1.5 py-px text-[11px] font-semibold tabular text-foreground/70">{count}</span> : null}
        </h2>
        {description ? <p className="mt-0.5 text-[12.5px] text-muted-foreground">{description}</p> : null}
      </div>
      {right}
    </div>
  );
}

function ViewAll({ to, children = "View all", testId }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 text-[12.5px] font-medium text-muted-foreground hover:text-primary" data-testid={testId}>
      {children} <ArrowRight className="h-3.5 w-3.5" />
    </Link>
  );
}

function Stat({ label, value, sub, accent, testId }) {
  return (
    <div data-testid={testId} className="min-w-0">
      <div className="eyebrow">{label}</div>
      <div className={cn("font-display mt-1.5 whitespace-nowrap text-[22px] font-semibold leading-none tabular tracking-[-0.03em] sm:text-[26px]", accent && "text-primary")} data-num="">{value}</div>
      {sub ? <div className="mt-1.5 text-[12px] text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

function dayLabel(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

function WeekStrip({ today, week }) {
  const days = useMemo(() => {
    const out = [];
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i += 1) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const count = i === 0 ? today.length : week.filter((m) => m.date === iso).length;
      out.push({ iso, wd: d.toLocaleDateString("en-GB", { weekday: "short" }).slice(0, 2), day: d.getDate(), count, isToday: i === 0, weekend: d.getDay() === 0 || d.getDay() === 6 });
    }
    return out;
  }, [today, week]);
  return (
    <div className="grid grid-cols-7 gap-1.5" data-testid="overview-week-strip" aria-hidden="true">
      {days.map((d) => (
        <div key={d.iso} className={cn("flex flex-col items-center rounded-xl py-2", d.isToday ? "bg-[hsl(var(--ink))] text-white" : "bg-black/[0.035]", d.weekend && !d.isToday && "opacity-60")}>
          <span className={cn("text-[10px] font-medium uppercase tracking-wide", d.isToday ? "text-white/60" : "text-muted-foreground")}>{d.wd}</span>
          <span className="mt-0.5 text-[14px] font-semibold tabular">{d.day}</span>
          <span className="mt-1 flex h-1.5 items-center gap-0.5">
            {Array.from({ length: Math.min(d.count, 3) }).map((_, i) => <span key={i} className={cn("h-1.5 w-1.5 rounded-full", d.isToday ? "bg-[hsl(10_80%_70%)]" : "bg-primary")} />)}
          </span>
        </div>
      ))}
    </div>
  );
}

function MeetingRow({ m, isNext, showDay }) {
  return (
    <Link to={m.opportunity_id ? `/opportunities/${m.opportunity_id}` : "#"} className="group flex gap-3.5 rounded-xl px-2 py-2.5 -mx-2 transition-colors hover:bg-black/[0.03]" data-testid="overview-meeting-row">
      <span className="w-14 shrink-0 pt-px font-mono text-[12px] tabular text-muted-foreground">{showDay ? dayLabel(m.date).split(" ")[0] : m.time}</span>
      <span className="relative mt-1.5 flex w-2.5 shrink-0 justify-center">
        <span className={cn("h-2 w-2 rounded-full", isNext ? "bg-primary shadow-[0_0_0_4px_hsl(10_76%_61%/0.16)]" : "bg-black/[0.18]")} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <CompanyLogo name={m.account_name} domain={m.account_website} size="sm" className="h-5 w-5 rounded-md p-0.5" />
          <span className="truncate text-[13.5px] font-semibold">{m.account_name}</span>
          {isNext ? <span className="rounded-full bg-primary/10 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-primary">Next</span> : null}
          {showDay ? <span className="ml-auto font-mono text-[11px] tabular text-muted-foreground">{m.time}</span> : null}
        </div>
        <div className="truncate text-[12.5px] text-muted-foreground">{m.title}</div>
        {m.location ? <div className="mt-0.5 flex items-center gap-1 text-[11.5px] text-muted-foreground/80"><MapPin className="h-3 w-3" /> {m.location}</div> : null}
      </div>
    </Link>
  );
}

export default function OverviewPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    setError(null);
    api.get("/overview").then((r) => setData(r.data)).catch((e) => setError(e));
  };
  useEffect(load, []);

  const firstName = (user?.name || "").split(" ")[0];
  return <OverviewBody data={data} error={error} onRetry={load} firstName={firstName} onOpen={(p) => navigate(p)} />;
}

/**
 * The overview itself, given its data. The page above fetches; static previews (the proposal) pass fixed data.
 * onOpen(path) is what a click on an attention row does.
 */
export function OverviewBody({ data, error, onRetry, firstName, onOpen = () => {} }) {
  const navigate = onOpen;
  const load = onRetry;
  const stageTotal = useMemo(() => (data?.stage_breakdown || []).reduce((s, x) => s + (x.value || 0), 0), [data]);
  const nextMeetingId = useMemo(() => {
    if (!data?.meetings?.length) return null;
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    return (data.meetings.find((m) => (m.time || "") >= hhmm) || null)?.id;
  }, [data]);
  const p = data?.pipeline;
  const week = data?.week || [];
  const activity = data?.recent_activity || [];
  const delta = p?.delta_30d || 0;
  const monthShort = (d) => new Date(`${d.date}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

  return (
    <div className="mx-auto max-w-[1280px] px-5 py-8 sm:px-8 lg:px-10 lg:py-10" data-testid="overview-body">
      {/* Header */}
      <div className="fade-up">
        <div className="eyebrow">{todayLong()}</div>
        <h1 className="mt-2.5 text-[34px] font-semibold leading-[1.05] tracking-[-0.03em] sm:text-[42px]" data-testid="overview-greeting">
          {greeting()}, {firstName}<span className="text-primary">.</span>
        </h1>
        {data ? (
          <p className="mt-2.5 max-w-2xl text-[14.5px] leading-relaxed text-muted-foreground" data-testid="overview-summary-line">
            <span className="font-semibold text-foreground tabular" data-num="">{data.attention.length}</span> {data.attention.length === 1 ? "opportunity needs" : "opportunities need"} attention
            <span className="mx-2 text-border">·</span>
            <span className="font-semibold text-foreground tabular" data-num="">{data.meetings.length}</span> {data.meetings.length === 1 ? "meeting" : "meetings"} today
            <span className="mx-2 text-border">·</span>
            <span className="font-semibold text-foreground tabular" data-num="">{fmtCompact(p.total_open)}</span> open across {p.open_count} opportunities
          </p>
        ) : (
          <Skeleton className="mt-3 h-5 w-[420px] max-w-full" />
        )}
      </div>

      {error && (
        <div className="mt-8 flex items-center justify-between gap-4 rounded-xl border border-destructive/30 bg-destructive/[0.05] px-4 py-3 text-sm text-destructive" data-testid="overview-error">
          <span>Could not load your overview.</span>
          <Button size="sm" variant="outline" onClick={load} className="gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> Retry</Button>
        </div>
      )}

      {/* Pipeline canvas ------------------------------------------------ */}
      <section className="fade-up d-1 mt-10" data-testid="overview-pipeline">
        {!p ? (
          <Skeleton className="h-64 rounded-2xl" />
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-4">
              <div className="eyebrow">Open pipeline</div>
              <div className="font-display mt-2 whitespace-nowrap text-[52px] font-semibold leading-none tabular tracking-[-0.04em] sm:text-[60px]" data-testid="overview-pipeline-total" data-num="">{fmtCompact(p.total_open)}</div>
              <div className="mt-3 flex items-center gap-2 text-[13px]" data-testid="overview-pipeline-delta">
                <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium tabular", delta >= 0 ? "bg-[hsl(var(--status-ok)/0.1)] text-[hsl(var(--status-ok))]" : "bg-[hsl(var(--status-bad)/0.1)] text-[hsl(var(--status-bad))]")}>
                  {delta >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                  <span data-num="">{delta >= 0 ? "+" : "−"}{fmtCompact(Math.abs(delta))}</span>
                </span>
                <span className="text-muted-foreground">last 30 days · {p.open_count} open</span>
              </div>
              <div className="mt-8 grid grid-cols-3 gap-4 sm:gap-6">
                <Stat label="Quotes out" value={fmtCompact(p.in_quotes)} sub="awaiting decision" accent testId="overview-pipeline-in-quotes" />
                <Stat label="Weighted" value={fmtCompact(p.weighted)} sub="by probability" testId="overview-pipeline-weighted" />
                <Stat label="Win rate" value={p.win_rate === null || p.win_rate === undefined ? "–" : `${p.win_rate}%`} sub="last 90 days" testId="overview-pipeline-win-rate" />
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="flex items-center justify-between">
                <span className="eyebrow">Pipeline value · 90 days</span>
                <ViewAll to="/pipeline" testId="overview-open-pipeline-link">Open pipeline</ViewAll>
              </div>
              <AreaTrend data={p.series || []} height={200} className="mt-2" formatX={monthShort} formatY={(v) => fmtCompact(v)} />
            </div>
          </div>
        )}

        {p && (
          <div className="mt-8" data-testid="overview-stage-bar">
            <div className="flex h-[6px] gap-[3px] overflow-hidden rounded-full">
              {(data.stage_breakdown || []).filter((s) => s.value > 0).map((s) => (
                <Link key={s.key} to={`/pipeline?stage=${s.key}`} title={`${s.label}: ${fmtCompact(s.value)} (${s.count})`} className="grow-x h-full rounded-full transition-opacity hover:opacity-80" style={{ width: `${Math.max(1.5, (s.value / (stageTotal || 1)) * 100)}%`, backgroundColor: stageColor(s.key) }} />
              ))}
              {stageTotal === 0 && <div className="h-full w-full rounded-full bg-muted" />}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
              {(data.stage_breakdown || []).map((s) => (
                <Link key={s.key} to={`/pipeline?stage=${s.key}`} className={cn("inline-flex items-center gap-1.5 text-[12px] hover:text-foreground", s.count === 0 ? "text-muted-foreground/60" : "text-muted-foreground")} data-testid={`overview-stage-legend-${s.key}`}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: stageColor(s.key) }} />
                  <span className="font-medium text-foreground/85">{s.label}</span>
                  <span className="tabular">{s.count}</span>
                  {s.value > 0 ? <span className="tabular text-muted-foreground/70">· {fmtCompact(s.value)}</span> : null}
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      <div className="hairline-x my-12" />

      {/* Main grid ------------------------------------------------------- */}
      <div className="grid grid-cols-1 gap-x-14 gap-y-12 lg:grid-cols-12">
        <div className="space-y-12 lg:col-span-7 xl:col-span-8">
          {/* Attention */}
          <section className="fade-up d-2">
            <SectionHead title="Needs your attention" description="Ranked by what the system thinks you should look at first." count={data ? data.attention.length : undefined} right={<ViewAll to="/pipeline" testId="overview-attention-view-all">Pipeline</ViewAll>} />
            {!data ? (
              <div className="divide-y divide-black/[0.06]">{[0, 1, 2].map((i) => <div key={i} className="flex gap-4 py-4"><Skeleton className="h-10 w-10 rounded-xl" /><div className="flex-1"><Skeleton className="h-4 w-2/3" /><Skeleton className="mt-2 h-3 w-1/2" /></div></div>)}</div>
            ) : data.attention.length === 0 ? (
              <EmptyState title="All clear" description="Nothing needs your attention right now." testId="overview-attention-empty" />
            ) : (
              <div className="divide-y divide-black/[0.06]" data-testid="overview-attention-list">
                {data.attention.map((a, i) => (
                  <div
                    key={a.opportunity_id}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/opportunities/${a.opportunity_id}?tab=${a.tab}`)}
                    onKeyDown={(e) => { if (e.key === "Enter") navigate(`/opportunities/${a.opportunity_id}?tab=${a.tab}`); }}
                    className="group -mx-3 flex cursor-pointer flex-col gap-3 rounded-2xl px-3 py-4 transition-colors hover:bg-black/[0.03] focus-visible:bg-black/[0.03] focus-visible:outline-none sm:flex-row sm:items-center sm:gap-4"
                    data-testid="overview-attention-card"
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-3.5">
                      <span className="mt-2.5 hidden w-4 shrink-0 font-mono text-[11px] tabular text-muted-foreground/60 sm:block">{String(i + 1).padStart(2, "0")}</span>
                      <CompanyLogo name={a.account_name} domain={a.account_website} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                          <span className="text-[14.5px] font-semibold tracking-[-0.01em]">
                            {a.account_name} <span className="font-medium text-muted-foreground">/ {a.name}</span>
                          </span>
                          <StageBadge stage={a.stage} className="h-5 whitespace-nowrap px-2 py-0 text-[11px]" />
                        </div>
                        <div className="mt-1 text-[13px] leading-snug text-muted-foreground">{a.lines.join(" · ")}</div>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center justify-between gap-3 pl-[54px] sm:flex-col sm:items-end sm:gap-1.5 sm:pl-0">
                      <Money value={a.value} compact className="text-[14px] font-semibold" />
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 group-hover:border-primary/50 group-hover:text-primary"
                        onClick={(e) => { e.stopPropagation(); navigate(`/opportunities/${a.opportunity_id}?tab=${a.tab}`); }}
                        data-testid="overview-attention-action-button"
                      >
                        {a.action_label} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Recent activity */}
          <section className="fade-up d-3">
            <SectionHead title="Recent activity" description="What happened across the pipeline lately." />
            {!data ? (
              <Skeleton className="h-48 rounded-2xl" />
            ) : activity.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">No activity yet.</div>
            ) : (
              <ol className="relative" data-testid="overview-activity-feed">
                <span className="absolute bottom-3 left-[15px] top-3 w-px bg-black/[0.08]" aria-hidden="true" />
                {activity.map((a) => {
                  const Icon = ACT_ICON[a.type] || Activity;
                  return (
                    <li key={a.id} className="relative flex gap-4 py-2.5" data-testid="overview-activity-item">
                      <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-black/[0.07] bg-white text-muted-foreground shadow-[0_1px_2px_hsl(30_12%_18%/0.04)]">
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0 flex-1 pt-1">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <Link to={a.opportunity_id ? `/opportunities/${a.opportunity_id}?tab=activity` : "#"} className="text-[13.5px] font-medium hover:text-primary">{a.title}</Link>
                          <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
                            {a.account_name ? <CompanyLogo name={a.account_name} domain={a.account_website} size="sm" className="h-4 w-4 rounded p-0" /> : null}
                            {a.account_name}{a.opportunity_name ? ` / ${a.opportunity_name}` : ""}
                          </span>
                        </div>
                        {a.detail ? <div className="mt-0.5 line-clamp-1 text-[12.5px] text-muted-foreground/90">{a.detail}</div> : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-2 pt-1 text-[11.5px] text-muted-foreground">
                        {a.user_initials ? <UserAvatar name={a.user_name} initials={a.user_initials} color={a.user_color} size="xs" /> : null}
                        <span className="tabular">{relTime(a.date)}</span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        </div>

        <aside className="space-y-12 lg:col-span-5 xl:col-span-4">
          {/* Agenda */}
          <section className="fade-up d-2">
            <SectionHead title="Agenda" description={data ? `${data.meetings.length} today · ${week.length} later this week` : undefined} />
            {!data ? (
              <Skeleton className="h-56 rounded-2xl" />
            ) : (
              <div data-testid="overview-meetings">
                <WeekStrip today={data.meetings} week={week} />
                <div className="mt-5">
                  <div className="eyebrow mb-1 text-[10px]">Today</div>
                  {data.meetings.length === 0 ? (
                    <div className="py-2 text-[13px] text-muted-foreground">No meetings today.</div>
                  ) : (
                    <div className="divide-y divide-black/[0.05]">{data.meetings.map((m) => <MeetingRow key={m.id} m={m} isNext={m.id === nextMeetingId} />)}</div>
                  )}
                </div>
                {week.length > 0 && (
                  <div className="mt-5" data-testid="overview-week-list">
                    <div className="eyebrow mb-1 text-[10px]">Later this week</div>
                    <div className="divide-y divide-black/[0.05]">{week.map((m) => <MeetingRow key={m.id} m={m} showDay />)}</div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* AI suggestions */}
          <section className="fade-up d-3">
            <SectionHead title="Suggested next moves" description="Patterns spotted across your pipeline." right={<Sparkles className="h-4 w-4 text-primary" />} />
            {!data ? (
              <Skeleton className="h-24 rounded-2xl" />
            ) : data.suggestions.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">No suggestions right now.</div>
            ) : (
              <div className="divide-y divide-black/[0.05]" data-testid="overview-suggestions">
                {data.suggestions.map((s, i) => (
                  <Link key={i} to={s.link} className="group -mx-2 flex items-start gap-3 rounded-xl px-2 py-3 hover:bg-black/[0.03]" data-testid="overview-suggestion">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><Sparkles className="h-3.5 w-3.5" /></span>
                    <span className="flex-1 text-[13px] leading-snug text-foreground/90">{s.text}</span>
                    <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-primary" />
                  </Link>
                ))}
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}
